import React, { useState, useEffect } from 'react';

const FAMILIES = ['Hip-Hop','Pop', 'Rock', 'Country', 'Electronic', 'R&B',];

export default function GenreRadar({ families = {}, size = 280, animate = true }) {
  const [scale, setScale] = useState(animate ? 0 : 1);

  useEffect(() => {
    if (animate) {
      const timer = setTimeout(() => setScale(1), 100);
      return () => clearTimeout(timer);
    }
  }, [animate]);

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 40; // Leave room for labels
  const angleStep = (Math.PI * 2) / 6;

  // Normalize so the max value reaches ~90% of the radius
  const maxVal = Math.max(...FAMILIES.map(f => families[f] || 0), 1);
  const normalizeVal = (val) => Math.max(8, (val / maxVal) * 90); // min 8% so zero isn't invisible

  const getPoint = (index, value) => {
    const angle = angleStep * index - Math.PI / 2;
    const r = (normalizeVal(value) / 100) * radius * scale;
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  };

  const getLabelPoint = (index) => {
    const angle = angleStep * index - Math.PI / 2;
    const r = radius + 24;
    return {
      x: cx + r * Math.cos(angle),
      y: cy + r * Math.sin(angle),
    };
  };

  // Guide hexagons
  const guideHexagon = (pct) => {
    return FAMILIES.map((_, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const r = (pct / 100) * radius;
      return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    }).join(' ');
  };

  // Data polygon
  const dataPolygon = FAMILIES.map((f, i) => {
    const val = families[f] || 0;
    const pt = getPoint(i, val);
    return `${pt.x},${pt.y}`;
  }).join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Guide hexagons */}
      {[25, 50, 75, 100].map(pct => (
        <polygon
          key={pct}
          points={guideHexagon(pct)}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="1"
        />
      ))}

      {/* Axis lines */}
      {FAMILIES.map((_, i) => {
        const angle = angleStep * i - Math.PI / 2;
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={cx + radius * Math.cos(angle)}
            y2={cy + radius * Math.sin(angle)}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        );
      })}

      {/* Data polygon */}
      <polygon
        points={dataPolygon}
        fill="rgba(139, 92, 246, 0.3)"
        stroke="rgba(139, 92, 246, 0.8)"
        strokeWidth="2"
        style={{ transition: 'all 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      />

      {/* Data points */}
      {FAMILIES.map((f, i) => {
        const val = families[f] || 0;
        const pt = getPoint(i, val);
        return (
          <circle
            key={f}
            cx={pt.x}
            cy={pt.y}
            r="3"
            fill="rgba(139, 92, 246, 1)"
            style={{ transition: 'all 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          />
        );
      })}

      {/* Labels */}
      {FAMILIES.map((f, i) => {
        const lp = getLabelPoint(i);
        const val = families[f] || 0;
        return (
          <text
            key={f}
            x={lp.x}
            y={lp.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontSize="10"
            fontWeight="700"
          >
            <tspan x={lp.x} dy="-0.5em">{f}</tspan>
            <tspan x={lp.x} dy="1.2em" fill="rgba(255,255,255,0.4)" fontSize="9">{val}%</tspan>
          </text>
        );
      })}
    </svg>
  );
}
